import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPurchaseRequestService } from '@/services/mock/MockPurchaseRequestService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { PurchaseRequest, BudgetSummary, DocumentAttachment } from '@/types/domain';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { BudgetSummaryWidget } from '@/components/data-display/BudgetSummaryWidget';
import { ClarificationBanner } from '@/components/data-display/ClarificationBanner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ROUTES } from '@/constants/routes';
import {
  Send,
  ArrowLeft,
  Database,
  AlertCircle,
  FileText,
  Building2,
  Tag,
  Layers,
  Download,
  Paperclip,
  IndianRupee,
  Eye,
  Calendar,
  LayoutGrid,
  Table as TableIcon,
} from 'lucide-react';
import { UnifiedProcurementLifecycleBar } from '@/components/navigation/UnifiedProcurementLifecycleBar';


export interface RequestDetailsPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const RequestDetailsPage: React.FC<RequestDetailsPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();

  const [request, setRequest] = useState<PurchaseRequest | null>(null);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemsView, setItemsView] = useState<'cards' | 'table'>(isDrawer ? 'cards' : 'table');

  const formatINR = (val: number) =>
    '₹' + val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const loadData = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const req = await mockPurchaseRequestService.getRequestById(id);
      setRequest(req);
      if (req?.costCenterCode) {
        const b = await mockPurchaseRequestService.getBudget(req.costCenterCode);
        setBudget(b);
      }
      const docs = await mockDocumentCommentService.getDocuments('REQUEST', id);
      setDocuments(docs);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleSubmitDraft = async () => {
    if (!request) return;
    try {
      setIsSubmitting(true);
      const idempotencyKey = `submit-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const updated = await mockPurchaseRequestService.submitRequest(request.id, idempotencyKey);
      setRequest(updated);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClarificationResponse = async (responseText: string) => {
    if (!request) return;
    const updated = await mockPurchaseRequestService.respondToClarification(request.id, responseText);
    setRequest(updated);
  };

  const handleDownloadFile = async (docId: string) => {
    try {
      const res = await mockDocumentCommentService.downloadDocument(docId);
      window.open(res.downloadUrl, '_blank');
    } catch {
      alert('Download failed. Unable to fetch attachment.');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-4 animate-pulse font-poppins">
        <div className="h-8 bg-muted rounded-xl w-1/3" />
        <div className="h-20 bg-muted rounded-xl w-full" />
        <div className="h-64 bg-muted rounded-xl w-full" />
        <div className="h-44 bg-muted rounded-xl w-full" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="max-w-md mx-auto my-12 text-center space-y-4 p-8 border border-border rounded-2xl bg-surface font-poppins">
        <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
        <h2 className="text-lg font-bold text-foreground">Requisition Not Found</h2>
        <p className="text-xs text-foreground-secondary">
          The requested requisition number ({id}) could not be located or you lack authorization to view it.
        </p>
        <Button size="sm" onClick={() => navigate(ROUTES.REQUESTS.LIST)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Return to My Requests
        </Button>
      </div>
    );
  }

  const activeDocuments = documents.filter((d) => d.status !== 'DELETED');

  return (
    <div className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'max-w-7xl mx-auto pb-12'}`}>
      {/* Enterprise Dossier Header Banner */}
      <div className="rounded-2xl border border-border/80 bg-muted/20 p-4 space-y-3 shadow-2xs">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1 min-w-0">
            {!isDrawer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(ROUTES.REQUESTS.LIST)}
                className="p-1 h-7 rounded-lg hover:bg-muted cursor-pointer text-xs font-bold text-foreground-secondary mb-1 inline-flex items-center gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to My Requests
              </Button>
            )}
            <span className="text-[10px] uppercase font-bold tracking-wider text-foreground-muted block">
              Requisition Code
            </span>
            <h1 className="text-xl sm:text-2xl font-black font-mono tracking-tight text-foreground">
              {request.requestNumber}
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <StatusBadge status={request.status} category="PurchaseRequest" className="shadow-2xs" />
            {request.priority && (
              <Badge
                variant={request.priority === 'High' ? 'destructive' : 'outline'}
                className="text-xs font-bold px-2.5 py-0.5 whitespace-nowrap shadow-2xs"
              >
                {request.priority} Priority
              </Badge>
            )}
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-foreground-muted bg-muted/70 px-2 py-0.5 rounded-md border border-border shrink-0">
              <Eye className="h-3 w-3" /> View Mode
            </span>
          </div>
        </div>

        <div className="pt-2.5 border-t border-border/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          <p className="text-xs sm:text-sm font-semibold text-foreground leading-snug">
            {request.title}
          </p>

          {request.status === 'Draft' && (
            <Button size="sm" isLoading={isSubmitting} onClick={handleSubmitDraft} className="shrink-0 font-bold">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Submit Requisition
            </Button>
          )}
        </div>
      </div>

      {/* End-to-End Enterprise Procurement Lifecycle Flow Tracker */}
      <UnifiedProcurementLifecycleBar
        requestId={request.id}
        onChainUpdate={async () => {
          const req = await mockPurchaseRequestService.getRequestById(request.id);
          if (req) setRequest(req);
        }}
      />

      {/* Unified Compact Metadata Bar */}
      <div className={`grid gap-2.5 p-3.5 rounded-2xl border border-border bg-card shadow-2xs text-xs font-poppins ${isDrawer ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
        <div className="space-y-1 p-2.5 rounded-xl bg-muted/30 border border-border/50">
          <span className="text-[10px] uppercase font-bold text-foreground-muted flex items-center gap-1.5">
            <Building2 className="h-3 w-3 text-primary shrink-0" /> Requester & Dept
          </span>
          <p className="font-bold text-foreground truncate" title={request.requesterName}>{request.requesterName}</p>
          <p className="text-[11px] text-foreground-secondary truncate" title={request.departmentName}>{request.departmentName}</p>
        </div>

        <div className="space-y-1 p-2.5 rounded-xl bg-muted/30 border border-border/50">
          <span className="text-[10px] uppercase font-bold text-foreground-muted flex items-center gap-1.5">
            <Layers className="h-3 w-3 text-primary shrink-0" /> Org & Cost Center
          </span>
          <p className="font-bold text-foreground truncate">Plant: {request.plantCode}</p>
          <p className="text-[11px] text-foreground-secondary font-mono truncate">CC: {request.costCenterCode}</p>
        </div>

        <div className="space-y-1 p-2.5 rounded-xl bg-muted/30 border border-border/50">
          <span className="text-[10px] uppercase font-bold text-foreground-muted flex items-center gap-1.5">
            <Tag className="h-3 w-3 text-primary shrink-0" /> Category & Type
          </span>
          <p className="font-bold text-foreground truncate">{request.category}</p>
          <p className="text-[11px] text-foreground-secondary truncate">
            {request.procurementType || 'Material'} • {request.expenseType || 'CAPEX'}
          </p>
        </div>

        <div className="space-y-1 p-2.5 rounded-xl bg-primary/5 border border-primary/20">
          <span className="text-[10px] uppercase font-bold text-primary flex items-center gap-1.5">
            <IndianRupee className="h-3 w-3 text-primary shrink-0" /> Total Amount
          </span>
          <p className="text-base font-extrabold font-mono text-primary truncate">
            {formatINR(request.totalAmount)}
          </p>
          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold truncate">
            {request.items.length} {request.items.length === 1 ? 'Line Item' : 'Line Items'} • Verified
          </p>
        </div>
      </div>

      {/* SAP ERP PR Synchronized Banner */}
      {request.sapPrNumber && (
        <div className="p-3.5 rounded-2xl border border-primary/25 bg-primary/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-2xs">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <Database className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-foreground truncate">SAP ERP Purchase Requisition Synchronized</p>
              <p className="text-[11px] text-foreground-secondary truncate">
                Downstream purchasing requisition active in SAP ECC/S4HANA.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="font-mono font-bold text-xs bg-background shrink-0 whitespace-nowrap shadow-2xs self-start sm:self-auto">
            SAP PR #{request.sapPrNumber}
          </Badge>
        </div>
      )}

      {/* Clarification Action Banner */}
      {request.status === 'ClarificationRequired' && request.clarificationMessage && (
        <ClarificationBanner
          message={request.clarificationMessage}
          requestedBy={request.clarificationRequestedBy}
          requestedAt={request.clarificationRequestedAt}
          onRespond={handleClarificationResponse}
        />
      )}

      {/* Business Justification & Uploaded Documents Cards */}
      <div className={isDrawer ? 'flex flex-col gap-3.5' : 'grid grid-cols-1 lg:grid-cols-12 gap-6'}>
        {/* Business Justification */}
        <div className={isDrawer ? 'w-full' : (activeDocuments.length > 0 ? 'lg:col-span-7' : 'lg:col-span-12')}>
          {request.justification && (
            <Card className="border border-border rounded-2xl shadow-2xs h-full">
              <CardHeader className="py-3 px-4 border-b border-border/60">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-primary" /> Business Justification & Purpose
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3.5 sm:p-4">
                <p className="text-xs text-foreground leading-relaxed bg-muted/30 p-3 rounded-xl border border-border/60">
                  {request.justification}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Uploaded Documents */}
        {activeDocuments.length > 0 && (
          <div className={isDrawer ? 'w-full' : 'lg:col-span-5'}>
            <Card className="border border-border rounded-2xl shadow-2xs h-full">
              <CardHeader className="py-3 px-4 border-b border-border/60 flex flex-row items-center justify-between">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-primary" /> Attached Documents ({activeDocuments.length})
                </CardTitle>
                <span className="text-[11px] font-mono text-foreground-secondary">
                  {(activeDocuments.reduce((acc, d) => acc + d.fileSize, 0) / (1024 * 1024)).toFixed(2)} MB
                </span>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {activeDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-2.5 rounded-xl border border-border bg-muted/20 hover:bg-muted/40 transition-colors flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <FileText className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate text-xs" title={doc.fileName}>
                          {doc.fileName}
                        </p>
                        <div className="flex items-center gap-1.5 text-[10px] text-foreground-secondary font-mono">
                          <span>{(doc.fileSize / 1024).toFixed(1)} KB</span>
                          <span>•</span>
                          <span className="capitalize">{doc.category.toLowerCase()}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadFile(doc.id)}
                      className="p-1 h-7 w-7 rounded-lg hover:bg-muted shrink-0 text-foreground-secondary hover:text-foreground cursor-pointer"
                      title={`Download ${doc.fileName}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Requisition Line Items Module with Dual Mode: Cards (Zero-scroll) or Table (Horizontal Scroll) */}
      <Card className="border border-border rounded-2xl shadow-2xs overflow-hidden font-poppins">
        <CardHeader className="py-3 px-4 border-b border-border/60 flex flex-row items-center justify-between bg-muted/10 gap-2 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-foreground truncate">
              Line Items ({request.items.length})
            </CardTitle>
            <span className="font-mono text-primary font-bold text-xs bg-primary/10 px-2.5 py-0.5 rounded-md border border-primary/20 shrink-0">
              {formatINR(request.totalAmount)}
            </span>
          </div>

          <div className="flex items-center gap-1 bg-muted/70 p-0.5 rounded-xl border border-border/60 shrink-0">
            <button
              type="button"
              onClick={() => setItemsView('cards')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                itemsView === 'cards'
                  ? 'bg-card text-foreground shadow-2xs border border-border/60'
                  : 'text-foreground-secondary hover:text-foreground'
              }`}
              title="Card View (Clean vertical flow without horizontal scroll)"
            >
              <LayoutGrid className="h-3 w-3" />
              <span>Cards</span>
            </button>
            <button
              type="button"
              onClick={() => setItemsView('table')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                itemsView === 'table'
                  ? 'bg-card text-foreground shadow-2xs border border-border/60'
                  : 'text-foreground-secondary hover:text-foreground'
              }`}
              title="Table View (Full multi-column spreadsheet matrix)"
            >
              <TableIcon className="h-3 w-3" />
              <span>Table</span>
            </button>
          </div>
        </CardHeader>

        {itemsView === 'cards' ? (
          /* Cards Mode: Zero horizontal scroll, fully aligned and stacked in length */
          <div className="p-3.5 sm:p-4 space-y-3 bg-muted/5">
            {request.items.map((item, idx) => (
              <div
                key={item.id}
                className="p-3.5 rounded-2xl border border-border bg-card shadow-2xs space-y-2.5 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="h-5 px-1.5 rounded-md bg-muted text-[10px] font-mono font-bold flex items-center justify-center text-foreground-secondary shrink-0">
                        #{item.itemNumber || idx + 1}
                      </span>
                      <span className="font-mono font-bold text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-md border border-primary/20 shrink-0">
                        {item.materialCode}
                      </span>
                    </div>
                    <h4 className="font-bold text-xs sm:text-sm text-foreground leading-snug">
                      {item.materialName}
                    </h4>
                  </div>

                  <div className="text-right shrink-0">
                    <span className="text-sm sm:text-base font-black font-mono text-primary block">
                      {formatINR(item.quantity * item.estimatedUnitPrice)}
                    </span>
                    <span className="text-[10px] text-foreground-muted font-mono font-medium block">
                      {item.quantity} {item.unitOfMeasure} × {formatINR(item.estimatedUnitPrice)}
                    </span>
                  </div>
                </div>

                {item.specifications && (
                  <p className="text-[11px] text-foreground-secondary bg-muted/30 p-2.5 rounded-xl border border-border/60 italic leading-relaxed">
                    {item.specifications}
                  </p>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border/50 text-[11px]">
                  <div className="space-y-0.5">
                    <span className="text-[10px] text-foreground-muted uppercase font-bold block">Quantity</span>
                    <span className="font-bold text-foreground font-mono">{item.quantity} {item.unitOfMeasure}</span>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[10px] text-foreground-muted uppercase font-bold block">Unit Price</span>
                    <span className="font-semibold text-foreground-secondary font-mono">{formatINR(item.estimatedUnitPrice)}</span>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[10px] text-foreground-muted uppercase font-bold block">GL Account</span>
                    <span className="font-mono font-semibold text-foreground">{item.glAccountCode || '500100'}</span>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[10px] text-foreground-muted uppercase font-bold block">Need By</span>
                    <span className="font-semibold text-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-primary shrink-0" /> {item.requiredDate}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Total Requisition Value Footer Card */}
            <div className="p-3.5 rounded-2xl border border-primary/20 bg-primary/5 flex items-center justify-between text-xs font-poppins">
              <span className="font-bold uppercase tracking-wider text-foreground-secondary">
                Total Requisition Amount ({request.items.length} {request.items.length === 1 ? 'Line Item' : 'Line Items'}):
              </span>
              <span className="text-base font-black font-mono text-primary">
                {formatINR(request.totalAmount)}
              </span>
            </div>
          </div>
        ) : (
          /* Table Mode: Clean horizontal scrolling with modern styling & whitespace-nowrap */
          <div className="relative">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-xs text-left min-w-[640px]">
                <thead className="bg-muted/60 border-b border-border text-[10px] uppercase tracking-wider text-foreground-secondary font-extrabold whitespace-nowrap">
                  <tr>
                    <th className="py-2.5 px-3 w-12 text-center">#</th>
                    <th className="py-2.5 px-3">Item & Description</th>
                    <th className="py-2.5 px-3 text-right">Qty</th>
                    <th className="py-2.5 px-3 text-right">Unit Price (INR)</th>
                    <th className="py-2.5 px-3 text-right">Total Price (INR)</th>
                    <th className="py-2.5 px-3">GL Account</th>
                    <th className="py-2.5 px-3">Need By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {request.items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-3 text-center font-mono font-bold text-foreground-secondary">
                        {item.itemNumber}
                      </td>
                      <td className="py-3 px-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono font-bold text-primary text-xs bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20">
                              {item.materialCode}
                            </span>
                            <span className="font-semibold text-foreground">{item.materialName}</span>
                          </div>
                          {item.specifications && (
                            <p className="text-[11px] text-foreground-secondary italic">
                              {item.specifications}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-foreground whitespace-nowrap">
                        {item.quantity} {item.unitOfMeasure}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-foreground-secondary whitespace-nowrap">
                        {formatINR(item.estimatedUnitPrice)}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-foreground whitespace-nowrap">
                        {formatINR(item.quantity * item.estimatedUnitPrice)}
                      </td>
                      <td className="py-3 px-3 font-mono text-foreground-secondary whitespace-nowrap">
                        {item.glAccountCode || '500100'}
                      </td>
                      <td className="py-3 px-3 text-foreground-secondary whitespace-nowrap">
                        {item.requiredDate}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/40 border-t border-border font-bold text-xs">
                  <tr>
                    <td colSpan={4} className="py-2.5 px-3 text-right text-foreground-secondary uppercase tracking-wider">
                      Total Requisition Value:
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-primary text-sm">
                      {formatINR(request.totalAmount)}
                    </td>
                    <td colSpan={2} className="py-2.5 px-3 text-[11px] text-foreground-secondary">
                      INR (₹)
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* Cost Center Budget Module */}
      <BudgetSummaryWidget
        budget={budget}
        requestAmount={request.totalAmount}
        currency="INR"
        horizontal={!isDrawer}
      />
    </div>
  );
};
